<template>
  <app-page labelledby="saldo-title" full-width>
    <page-header
      title-id="saldo-title"
      title="Оборотно-сальдовая ведомость"
      description="Регистр сальдо и оборотов по выбранному счету и аналитике."
    >
      <template #metadata>
        <span><strong>Период:</strong> {{ periodLabel }}</span>
        <span v-if="billName" class="saldo-account"><strong>Счет:</strong> {{ billName }}</span>
      </template>
      <template #actions>
        <report-artifact-actions
          :payload="artifactPayload"
          :org-id="connection.org_id"
          :disabled="loading || !generated || Boolean(resultState)"
          neutral
        />
        <q-btn flat no-caps icon="download" label="CSV" :disable="!flatRows.length" data-testid="saldo-export" @click="exportRows" />
      </template>
    </page-header>

    <page-section title="Параметры" description="Выберите счет, период и аналитику для ведомости.">
      <div class="saldo-filters" role="search" aria-label="Параметры оборотно-сальдовой ведомости">
        <q-select
          v-model="selectedBill"
          outlined
          dense
          use-input
          input-debounce="200"
          :options="filteredBillOptions"
          label="Счет"
          option-label="full_name"
          @filter="filterBills"
          @update:model-value="onBillChanged"
          data-testid="saldo-account-select"
        />
        <date-range-filter v-model="period" label="Период" />
        <q-select v-model="firstGrouping" outlined dense :options="groupingOptions" option-label="label" label="Первый уровень" />
        <q-select v-model="secondGrouping" outlined dense :options="secondGroupingOptions" option-label="label" label="Второй уровень" />
        <q-select
          v-model="firstLevelFilter"
          outlined
          dense
          :options="firstLevelOptions"
          option-label="label"
          label="Значение субконто"
          :disable="firstLevelOptions.length <= 1"
        />
        <q-btn
          outline
          no-caps
          label="Сформировать"
          :loading="loading"
          :disable="!selectedBill || loading"
          data-testid="saldo-generate"
          @click="generate"
        />
      </div>
    </page-section>

    <app-state
      v-if="accountsLoading"
      state="loading"
      title="Загружаем счета"
      message="Получаем доступный план счетов."
    />
    <app-state
      v-else-if="accountsError"
      state="error"
      title="Список счетов недоступен"
      :message="accountsError"
      @retry="loadAccounts"
    />
    <template v-else>
      <app-state
        v-if="!generated && !loading"
        state="empty"
        title="Ведомость еще не сформирована"
        message="Выберите параметры и нажмите «Сформировать»."
        data-testid="saldo-initial-state"
      />
      <app-state
        v-else-if="loading"
        state="loading"
        title="Формируем ОСВ"
        message="Подготавливаем сальдо и обороты."
        data-testid="saldo-loading"
      />
      <app-state
        v-else-if="resultState"
        :state="resultState"
        :title="resultState === 'access-denied' ? 'Недостаточно прав' : (resultState === 'not-found' ? 'Счет не найден' : 'Не удалось сформировать ОСВ')"
        :message="resultError"
        action-label="Повторить"
        @action="generate"
        data-testid="saldo-error-state"
      />
      <template v-else>
        <page-section title="Ведомость" description="Ноль является значением; отсутствующее поле обозначается тире.">
          <app-data-table
            title="Оборотно-сальдовая ведомость"
            :count="flatRows.length"
            :rows="flatRows"
            :columns="columns"
            :pagination="pagination"
            :show-count="false"
            :state="flatRows.length ? 'ready' : 'empty'"
            state-title="За период нет оборотов"
            state-message="За выбранный период нет оборотов."
            storage-key="finance-saldo"
            data-testid="saldo-table"
          >
            <template #header="props">
              <q-tr :props="props" class="saldo-header saldo-header--groups">
                <q-th rowspan="2" class="text-left">Счет / субконто / документ</q-th>
                <q-th colspan="2">Сальдо на начало</q-th>
                <q-th colspan="2">Обороты за период</q-th>
                <q-th colspan="2">Сальдо на конец</q-th>
                <q-th rowspan="2" aria-label="Источник" />
              </q-tr>
              <q-tr :props="props" class="saldo-header saldo-header--columns">
                <q-th>Дебет</q-th>
                <q-th>Кредит</q-th>
                <q-th>Дебет</q-th>
                <q-th>Кредит</q-th>
                <q-th>Дебет</q-th>
                <q-th>Кредит</q-th>
              </q-tr>
            </template>
            <template #body-cell-label="props">
              <q-td :props="props">
                <div class="saldo-label" :style="{ '--saldo-level': props.row.level }">
                  <div><strong>{{ props.row.label }}</strong><span>{{ rowTypeLabel(props.row) }}</span></div>
                </div>
              </q-td>
            </template>
            <template v-for="column in amountColumns" #[`body-cell-${column}`]="props" :key="column">
              <q-td :props="props">
                <amount-cell :value="props.row[column]" />
                <small v-if="shouldShowCount">{{ displayValue(props.row[`${column}_count`]) }}</small>
              </q-td>
            </template>
            <template #body-cell-actions="props">
              <q-td :props="props" class="text-right">
                <q-btn
                  v-if="transferId(props.row)"
                  flat
                  no-caps
                  dense
                  label="Источник"
                  :to="{ name: 'bill_operation_info', params: { id: transferId(props.row) } }"
                />
              </q-td>
            </template>
            <template #bottom-row>
              <q-tr class="saldo-total-row" data-testid="saldo-summary">
                <q-td class="text-left">Итого</q-td>
                <q-td v-for="column in amountColumns" :key="column" class="text-right">
                  <amount-cell :value="summary[`saldo_${column}`]" />
                  <small v-if="shouldShowCount">{{ displayValue(summary[`saldo_${column}_count`]) }}</small>
                </q-td>
                <q-td />
              </q-tr>
            </template>
          </app-data-table>
        </page-section>
      </template>
    </template>
  </app-page>
</template>

<script>
import AppDataTable from '../../../components/ui/AppDataTable.vue'
import AmountCell from '../../../components/ui/AmountCell.vue'
import AppPage from '../../../components/ui/AppPage.vue'
import AppState from '../../../components/ui/AppState.vue'
import DateRangeFilter from '../../../components/ui/DateRangeFilter.vue'
import PageHeader from '../../../components/ui/PageHeader.vue'
import PageSection from '../../../components/ui/PageSection.vue'
import ReportArtifactActions from '../../../components/documents/ReportArtifactActions.vue'
import { formatDate, toIsoDate, unixEndOfDay, unixStartOfDay } from '../../../services/dateFormat'
import {
  downloadCsv,
  flattenSaldoRows,
  normalizeCompactBillsResponse,
  normalizeSaldoResponse
} from '../../../services/financeCore'

const fallbackGroupings = Object.freeze([
  { label: 'Продавец', value: 'seller_inn' },
  { label: 'Покупатель', value: 'cust_inn' },
  { label: 'Товар', value: 'name_id' },
  { label: 'Расчетный счет', value: 'account' }
])
const secondGroupingOptions = Object.freeze([
  { label: 'Документ', value: 'document' },
  { label: 'Без второго уровня', value: 'none' }
])
const amountColumns = Object.freeze(['debet_start', 'credit_start', 'debet_period', 'credit_period', 'debet_end', 'credit_end'])
const columns = Object.freeze([
  { name: 'label', label: 'Счет / субконто / документ', field: 'label', align: 'left', required: true },
  { name: 'debet_start', label: 'Начало Дт', field: 'debet_start', align: 'right' },
  { name: 'credit_start', label: 'Начало Кт', field: 'credit_start', align: 'right' },
  { name: 'debet_period', label: 'Оборот Дт', field: 'debet_period', align: 'right' },
  { name: 'credit_period', label: 'Оборот Кт', field: 'credit_period', align: 'right' },
  { name: 'debet_end', label: 'Конец Дт', field: 'debet_end', align: 'right' },
  { name: 'credit_end', label: 'Конец Кт', field: 'credit_end', align: 'right' },
  { name: 'actions', label: 'Действия', field: 'actions', align: 'right', required: true }
])

export default {
  name: 'BillSaldoMaster',
  components: { AppDataTable, AmountCell, AppPage, AppState, DateRangeFilter, PageHeader, PageSection, ReportArtifactActions },
  data() {
    const stored = this.$store.state.filter_period
    return {
      columns,
      amountColumns,
      secondGroupingOptions,
      bills: [],
      billOptions: [],
      filteredBillOptions: [],
      selectedBill: null,
      groupingOptions: [...fallbackGroupings],
      firstGrouping: fallbackGroupings[0],
      secondGrouping: secondGroupingOptions[0],
      firstLevelOptions: [{ label: 'Все значения', value: '__all' }],
      firstLevelFilter: { label: 'Все значения', value: '__all' },
      period: { from: toIsoDate(stored.from), to: toIsoDate(stored.to) },
      rows: [],
      summary: {},
      billName: '',
      shouldShowCount: false,
      accountsLoading: true,
      accountsError: '',
      loading: false,
      generated: false,
      resultState: '',
      resultError: '',
      pagination: { page: 1, rowsPerPage: 0 }
    }
  },
  computed: {
    periodLabel() {
      return `${formatDate(this.period.from, '—')} — ${formatDate(this.period.to, '—')}`
    },
    artifactPayload() {
      return {
        org_id: this.connection.org_id,
        report_code: 'trial_balance',
        period_start: this.period.from,
        period_end: this.period.to,
        date: this.period.to,
        bill_id: this.selectedBill && this.selectedBill.id,
        filter: this.firstGrouping && this.firstGrouping.value,
        filter2: this.secondGrouping && this.secondGrouping.value,
        first_level_filter: this.firstLevelFilter && this.firstLevelFilter.value,
        unit: 'rub',
        currency: 'RUB'
      }
    },
    flatRows() {
      return flattenSaldoRows(this.rows)
    },
  },
  created() {
    this.emitter.on('didReceiveBillListFlatCompact', this.onAccounts)
    this.emitter.on('didReceiveBillSaldoList', this.onSaldo)
    this.loadAccounts()
  },
  beforeUnmount() {
    this.emitter.off('didReceiveBillListFlatCompact', this.onAccounts)
    this.emitter.off('didReceiveBillSaldoList', this.onSaldo)
  },
  methods: {
    whenReady(request) {
      if (this.connection.isAuthenticated) request()
      else {
        const ready = () => {
          this.emitter.off('connectionReady', ready)
          request()
        }
        this.emitter.on('connectionReady', ready)
      }
    },
    loadAccounts() {
      this.accountsLoading = true
      this.accountsError = ''
      this.whenReady(() => this.connection.gitBillsListFlatCompact('saldo-accounts'))
    },
    onAccounts(response) {
      const result = normalizeCompactBillsResponse(response)
      this.accountsLoading = false
      if (!result.ok) {
        this.accountsError = result.message
        return
      }
      this.bills = result.bills
      this.billOptions = result.bills
      this.filteredBillOptions = result.bills
      const routeId = this.$route.params.id
      this.selectedBill = result.bills.find((bill) => String(bill.id) === String(routeId)) || result.bills[0] || null
      this.onBillChanged(this.selectedBill)
      if (routeId && this.selectedBill) this.generate()
    },
    filterBills(value, update) {
      update(() => {
        const query = String(value || '').trim().toLocaleLowerCase('ru-RU')
        this.filteredBillOptions = query
          ? this.billOptions.filter((bill) => bill.full_name.toLocaleLowerCase('ru-RU').includes(query))
          : this.billOptions
      })
    },
    onBillChanged(bill) {
      const fields = bill && bill.analytic_fields.length ? bill.analytic_fields : fallbackGroupings
      this.groupingOptions = fields.map((item) => ({ label: String(item.label || item.value), value: item.value }))
      this.firstGrouping = this.groupingOptions[0] || fallbackGroupings[0]
      this.firstLevelOptions = [{ label: 'Все значения', value: '__all' }]
      this.firstLevelFilter = this.firstLevelOptions[0]
      this.generated = false
      this.rows = []
      this.summary = {}
      this.resultState = ''
      this.resultError = ''
    },
    generate() {
      if (!this.selectedBill) return
      this.loading = true
      this.generated = true
      this.resultState = ''
      this.resultError = ''
      this.$store.commit('updateFilterPeriod', this.period)
      this.connection.getBillSaldoList(
        'saldo-report',
        unixStartOfDay(this.period.from),
        unixEndOfDay(this.period.to),
        this.selectedBill.id,
        this.firstGrouping.value,
        this.secondGrouping.value,
        this.firstLevelFilter.value
      )
    },
    onSaldo(response) {
      const result = normalizeSaldoResponse(response)
      this.loading = false
      if (!result.ok) {
        const error = String(response && response.error || '')
        this.resultState = error.includes('access_denied') ? 'access-denied' : (error.includes('not_found') ? 'not-found' : 'error')
        this.resultError = result.message
        this.rows = []
        return
      }
      this.rows = result.rows
      this.summary = result.summary
      this.billName = result.billName
      this.shouldShowCount = result.shouldShowCount
      this.firstLevelOptions = [{ label: 'Все значения', value: '__all' }, ...result.filterValues.map((value) => ({ label: value, value }))]
      if (!this.firstLevelOptions.some((item) => item.value === this.firstLevelFilter.value)) this.firstLevelFilter = this.firstLevelOptions[0]
    },
    displayValue(value) {
      return value === undefined || value === null || value === '' ? '—' : value
    },
    rowTypeLabel(row) {
      return { bill: 'Счет', document: 'Документ', transfer: 'Проводка', position: 'Номенклатура', account: 'Расчетный счет', contragent: 'Контрагент' }[row.model_type] || 'Аналитика'
    },
    transferId(row) {
      const match = row.model_type === 'transfer' && String(row.id).match(/^transfer-(\d+)$/)
      return match ? match[1] : null
    },
    exportRows() {
      downloadCsv(`saldo-${this.selectedBill ? this.selectedBill.name : 'report'}.csv`, [
        { name: 'label', label: 'Счет / субконто / документ' },
        ...amountColumns.map((name) => ({ name, label: columns.find((column) => column.name === name).label }))
      ], this.flatRows)
    }
  }
}
</script>

<style scoped>
.saldo-account { margin-left: var(--sid-space-4); }
.saldo-filters { display: grid; grid-template-columns: minmax(16rem, 2fr) auto repeat(3, minmax(10rem, 1fr)) auto; align-items: start; gap: var(--sid-space-3); }
.saldo-label { padding-left: calc(var(--saldo-level) * var(--sid-space-5)); }
.saldo-label strong, .saldo-label span { display: block; }
.saldo-label span { color: var(--sid-color-text-secondary); font-size: var(--sid-font-size-label); }
.q-td small { display: block; color: var(--sid-color-text-secondary); text-align: right; }
.saldo-header th, .saldo-total-row td { border-color: var(--sid-color-line-strong); }
.saldo-header--groups th { top: 0 !important; text-align: center; }
.saldo-header--groups th.text-left { text-align: left; }
.saldo-header--columns th { top: var(--sid-row-compact) !important; text-align: center; }
.saldo-total-row { font-weight: var(--sid-font-weight-semibold); }
@media (max-width: 1399px) {
  .saldo-filters { grid-template-columns: repeat(3, minmax(0, 1fr)); }
}
@media (max-width: 767px) {
  .saldo-filters { grid-template-columns: 1fr; }
  .saldo-filters :deep(.date-range-filter), .saldo-filters :deep(.q-btn) { width: 100%; }
  .saldo-label { padding-left: 0; }
}
</style>
